import React, { useEffect, useRef, useState } from "react";
import { Bell, BellRing, Trash2, ExternalLink, X } from "lucide-react";
import { Link } from "react-router-dom";
import { getList } from "@/lib/storage";

/**
 * NotificationsBell — topbar component.
 * Shows a unified inbox of: new alerts you triggered, saved-search refreshes,
 * application status nudges. Reads exclusively from localStorage so it's keyless.
 */
export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [seen, setSeen] = useState(() => {
    try {
      return Number(window.localStorage.getItem("jh_notif_seen") || "0");
    } catch {
      return 0;
    }
  });
  const ref = useRef(null);

  const rebuild = () => {
    const alerts = getList("alerts").map((a) => ({
      id: `alert:${a.url}`,
      ts: a.alertAt || 0,
      icon: "alert",
      title: `Alerting: ${a.title}`,
      body: `${a.company} · ${a.location || "—"}`,
      url: a.url,
    }));
    const apps = getList("apps").map((a) => ({
      id: `app:${a.url}`,
      ts: a.updatedAt || a.appliedAt || 0,
      icon: "app",
      title: `${a.title} — ${a.status}`,
      body: a.company,
      url: a.url,
    }));
    setItems([...alerts, ...apps].sort((a, b) => b.ts - a.ts).slice(0, 40));
  };

  useEffect(() => {
    rebuild();
    const h = () => rebuild();
    window.addEventListener("jh:storage", h);
    return () => window.removeEventListener("jh:storage", h);
  }, []);

  useEffect(() => {
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen((o) => {
          if (o) markRead();
          return false;
        });
      }
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const unread = items.filter((i) => i.ts > seen).length;

  const markRead = () => {
    const now = Date.now();
    setSeen(now);
    try {
      window.localStorage.setItem("jh_notif_seen", String(now));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => {
          setOpen((o) => {
            // Mark read only when the user CLOSES the panel — so the
            // "Mark all read" affordance stays reachable while open.
            if (o) markRead();
            return !o;
          });
        }}
        aria-label="Notifications"
        data-testid="topbar-bell"
        className="relative w-10 h-10 border-2 border-[#0a0a0a] bg-white inline-flex items-center justify-center hover-pop shadow-brutal-sm"
      >
        {unread > 0 ? <BellRing size={15} strokeWidth={3} /> : <Bell size={15} strokeWidth={3} />}
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[#E10600] text-white border-2 border-[#0a0a0a] font-accent text-[9px] flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="topbar-bell-panel"
          className="absolute right-0 z-40 mt-2 w-80 max-w-[92vw] bg-white border-4 border-[#0a0a0a] shadow-brutal-sm"
        >
          <div className="flex items-center justify-between px-3 py-2 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px]">
            <span>NOTIFICATIONS</span>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button
                  onClick={markRead}
                  data-testid="bell-mark-all-read"
                  className="text-[#FFC400]/85 hover:text-[#FFC400] hover:underline"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => {
                  markRead();
                  setOpen(false);
                }}
                aria-label="Close"
                data-testid="bell-close"
              >
                <X size={12} strokeWidth={3} />
              </button>
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center font-mono-body text-[12px] text-[#0a0a0a]/55">
                No notifications yet. Star a role or log an application to populate this inbox.
              </div>
            ) : (
              items.map((n) => (
                <a
                  key={n.id}
                  href={n.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-3 py-2.5 border-b-2 border-[#0a0a0a]/10 last:border-b-0 hover:bg-[#FDFBF7]"
                >
                  <div className="font-mono-body text-[12px] font-bold flex items-center gap-1.5">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: n.icon === "alert" ? "#FFC400" : "#E10600" }}
                    />
                    <span className="truncate">{n.title}</span>
                  </div>
                  <div className="font-mono-body text-[11px] text-[#0a0a0a]/65 truncate">{n.body}</div>
                  <div className="font-mono-body text-[10px] text-[#0a0a0a]/45">
                    {n.ts ? new Date(n.ts).toLocaleString() : ""}
                  </div>
                </a>
              ))
            )}
          </div>
          <div className="px-3 py-2 border-t-2 border-[#0a0a0a]/10 flex items-center justify-between">
            <Link
              to="/dashboard/alerts"
              onClick={() => setOpen(false)}
              className="font-accent text-[11px] hover:underline"
            >
              Manage alerts →
            </Link>
            <Link
              to="/dashboard/applications"
              onClick={() => setOpen(false)}
              className="font-accent text-[11px] hover:underline"
            >
              Open tracker →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
